import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { z } from 'zod';

import type { PrimeRate, UpdatePrimeRateInput } from '../../../__generated__/graphql/api';
import type { ModalProps } from '../../types/modal.types';

import { ListPrimeRatesDocument, useUpdatePrimeRateMutation } from '../../../__generated__/graphql/api';
import { createFormComponent } from '../../utils/create-form-component';

const updatePrimeRateSchema = z.object({
  primeRateId: z.string().min(1, 'Prime rate id is required'),
  effectiveDate: z.string().min(1, 'Effective date is required'),
  rate: z
    .string()
    .min(1, 'Rate is required')
    .regex(/^\d+(\.\d+)?$/, 'Rate must be a valid number')
    .refine((val) => parseFloat(val) >= 0 && parseFloat(val) <= 100, 'Rate must be between 0 and 100'),
  source: z.string().optional(),
});

type UpdatePrimeRateFormData = z.infer<typeof updatePrimeRateSchema>;

export const EditPrimeRateComponent = ({ payload, api }: ModalProps<{ primeRate: PrimeRate }, true>) => {
  const { primeRate } = payload;
  const { handleMutation } = useMutationWithToast();

  const [updatePrimeRate, { loading }] = useUpdatePrimeRateMutation({
    refetchQueries: [{ query: ListPrimeRatesDocument }],
  });

  if (!primeRate?.id) {
    return <div>Prime rate not found.</div>;
  }

  const handleSubmit = (data: UpdatePrimeRateFormData) => {
    const input: UpdatePrimeRateInput = {
      effectiveDate: data.effectiveDate,
      rate: parseFloat(data.rate),
      source: data.source?.trim() || undefined,
    };

    void handleMutation({
      mutation: () =>
        updatePrimeRate({
          variables: {
            id: primeRate.id,
            input,
          },
        }),
      extractSuccess: (result) => !!result?.data?.updatePrimeRate,
      debugLabel: 'update-prime-rate',
      toast: {
        success: 'Prime rate updated successfully',
        error: 'Failed to update prime rate',
        loading: 'Updating prime rate...',
      },
      onSuccess: () => {
        api.close(true);
      },
    });
  };

  const formConfig: SmartFormConfig<UpdatePrimeRateFormData> = {
    fields: [
      {
        name: 'primeRateId',
        label: 'Prime Rate ID',
        type: 'text',
        defaultValue: primeRate.id,
        readOnly: true,
      },
      {
        name: 'effectiveDate',
        label: 'Effective Date',
        type: 'date',
        defaultValue: primeRate.effectiveDate || '',
      },
      {
        name: 'rate',
        label: 'Rate (%)',
        type: 'text',
        defaultValue: primeRate.rate || '',
        placeholder: 'e.g., 8.50',
      },
      {
        name: 'source',
        label: 'Source',
        type: 'text',
        defaultValue: primeRate.source || '',
        placeholder: 'e.g., WSJ',
      },
    ],
    schema: updatePrimeRateSchema,
    defaultValues: {
      primeRateId: primeRate.id,
      effectiveDate: primeRate.effectiveDate || '',
      rate: primeRate.rate || '',
      source: primeRate.source || '',
    },
  };

  return createFormComponent({
    config: formConfig,
    handleSubmit,
    loading,
    mode: 'edit',
  });
};
